import {gql} from '@apollo/client';
import {pick} from '@benchling/js-utils';

import {convertMolBioStrandToGQLStrand} from 'constants/MolbioConstants';
import {SyncedModelConfig, SyncedModelName} from 'graphql-client/backbone-graphql-sync/SyncedModels';
import FragmentWrapper from 'graphql-client/FragmentWrapper';
import {ComponentSequencePrimerGraphQL} from 'molbio/primers/backbone/graphql/__generated/ComponentSequencePrimerGraphQL';
import SequencePrimer, {PRIMER_PANEL_PRIMER_CHECKED} from 'molbio/primers/backbone/SequencePrimer';

const COMPONENT_SEQUENCE_PRIMER_FRAGMENT = gql`
  fragment ComponentSequencePrimerGraphQL on ComponentSequencePrimerNode {
    active @client
    bases @client
    checked @client
    displayLabel @client
    displayMeltingTemperature @client
    end @client
    id @client
    isNew @client
    isArchived @client
    modifiedNucleotides @client {
      position @client
      sugarMonomerId @client
      phosphateMonomerId @client
      baseMonomerId @client
    }
    name @client
    nucleotideType @client
    oligoId @client
    partnerSelected @client
    selected @client
    start @client
    strand @client
  }
`;

export const ComponentSequencePrimerFragment: FragmentWrapper<ComponentSequencePrimerGraphQL> = {
  fragment: COMPONENT_SEQUENCE_PRIMER_FRAGMENT,
  fragmentName: 'ComponentSequencePrimerGraphQL',
};

export const ComponentSequencePrimerSyncConfig: SyncedModelConfig<SyncedModelName.COMPONENT_SEQUENCE_PRIMER> =
  {
    __typename: 'ComponentSequencePrimerNode',

    fragmentWrapper: ComponentSequencePrimerFragment,

    getGraphQLAttrs(sequencePrimer: SequencePrimer): ComponentSequencePrimerGraphQL {
      return {
        __typename: this.__typename,
        id: this.getId(sequencePrimer),
        active: sequencePrimer.active(),
        bases: sequencePrimer.bases(),
        checked: sequencePrimer.get(PRIMER_PANEL_PRIMER_CHECKED) ?? null,
        displayLabel: sequencePrimer.displayLabel(),
        displayMeltingTemperature: sequencePrimer.displayMeltingTemperature(),
        end: sequencePrimer.end(),
        modifiedNucleotides: sequencePrimer
          .get('_primer')
          .modifiedNucleotides()
          .map((n) => ({
            __typename: 'ModifiedNucleotideNode',
            ...pick(n, 'position', 'sugarMonomerId', 'phosphateMonomerId', 'baseMonomerId'),
          })),
        name: sequencePrimer.name(),
        nucleotideType: sequencePrimer.nucleotideType(),
        isNew: sequencePrimer.get('_primer').isNew(),
        isArchived: sequencePrimer.isArchived(),
        oligoId: sequencePrimer.get('_primer').get('primer_api_id') ?? null,
        partnerSelected: sequencePrimer.partnerSelected(),
        selected: sequencePrimer.selected(),
        start: sequencePrimer.start(),
        strand: convertMolBioStrandToGQLStrand(sequencePrimer.strand()),
      };
    },

    getId(sequencePrimer: SequencePrimer): string {
      // sequencePrimers don't have an id when they're new, or when they're on a part. Therefore, we need
      // a different stable Apollo cache identifier, so we're going with the Backbone cid
      return sequencePrimer.cid;
    },
  };
